import { BookmarkCheck, CircleX, Settings2, ShieldCheck, TriangleAlert, type LucideIcon } from "lucide-react";
import { useEffect, useId, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaCheckboxField } from "@/shared/components/forms/ColocaCheckboxField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaRadioGroupField } from "@/shared/components/forms/ColocaRadioGroupField";
import { ColocaSelect } from "@/shared/components/forms/ColocaSelect";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { CONCEPT_MAX_LENGTH, SAVED_WALLET_ALIAS_MAX_LENGTH } from "../../constants/cryptoSwaps.constants";
import { useCryptoSwapLabels } from "../../hooks/useCryptoSwapLabels";
import type { OtcSavedWallet } from "../../types/cryptoSwaps.types";
import { checkCryptoSwapAddress } from "../../utils/cryptoSwapAddress";
import { NetworkIcon } from "../shared/NetworkIcon";
import {
  buildDestinationSchema,
  EMPTY_DESTINATION,
  type DestinationMessages,
  type DestinationValues,
} from "./destination.schema";

const NEW_ADDRESS = "__new__";

/**
 * `destination` is where a purchase delivers; `refund` is the organization's own wallet a sale is sent from,
 * where the deposit returns if the sale does not complete. Always its own, so ownership is not asked.
 */
export type WalletSectionVariant = "destination" | "refund";

export interface DestinationSectionProps {
  network: string;
  /** The asset moved on-chain, named in the title and the warning. */
  asset: string;
  variant?: WalletSectionVariant;
  onChange: (values: DestinationValues, isValid: boolean) => void;
  disabled?: boolean;
  /** The organization's saved wallets on any network; only those on `network` are offered. */
  savedWallets?: OtcSavedWallet[];
  /** Saving needs `payments:write`; without it the option is not offered. */
  canSave?: boolean;
  onManageSaved?: () => void;
}

/** The checksum catches a mistyped address; the warning, a wallet on another network that would lose it. */
export function DestinationSection({
  network,
  asset,
  variant = "destination",
  onChange,
  disabled,
  savedWallets = [],
  canSave = false,
  onManageSaved,
}: DestinationSectionProps) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const titleId = useId();
  const networkShort = labels.networkShort(network);
  const refund = variant === "refund";

  const messages = useMemo<DestinationMessages>(
    () => ({
      addressRequired: t(refund ? "sell_refund_address_empty_hint" : "destination_address_empty_hint"),
      addressInvalid: t("destination_address_invalid", { network: networkShort }),
      networkUnsupported: t("destination_network_unsupported"),
      conceptTooLong: t("concept_too_long", { max: CONCEPT_MAX_LENGTH }),
      aliasTooLong: t("saved_wallet_alias_too_long", { max: SAVED_WALLET_ALIAS_MAX_LENGTH }),
      holderRequired: t("saved_wallet_holder_required"),
    }),
    [t, networkShort, refund],
  );

  const schema = useMemo(() => buildDestinationSchema(network, messages), [network, messages]);
  const form = useZodForm(schema, EMPTY_DESTINATION);
  const usable = useMemo(
    () => savedWallets.filter((wallet) => wallet.network === network),
    [savedWallets, network],
  );

  useEffect(() => {
    const emit = (values: DestinationValues) => onChange(values, schema.safeParse(values).success);
    emit(form.getValues() as DestinationValues);
    const subscription = form.watch((values) => emit(values as DestinationValues));
    return () => subscription.unsubscribe();
  }, [form, schema, onChange]);

  // A network change keeps the typed address (EVM on every offered chain); its verdict is re-read for it.
  useEffect(() => {
    if (form.getFieldState("address").isTouched) void form.trigger("address");
  }, [form, schema]);

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={titleId} className="text-lg font-semibold tracking-tight text-foreground">
          {refund ? t("sell_refund_title") : t("destination_title", { asset })}
        </h2>
        {onManageSaved && usable.length > 0 && (
          <ColocaButton size="sm" variant="ghost" icon={Settings2} onClick={onManageSaved}>
            {t("saved_wallets_manage")}
          </ColocaButton>
        )}
      </div>

      {refund ? (
        <p className="-mt-2 text-sm leading-relaxed text-muted-foreground">
          {t("sell_refund_hint", { asset })}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
          <NetworkIcon network={network} size={18} />
          <span className="text-muted-foreground">{t("destination_network_label")}</span>
          <span className="font-semibold text-foreground">{labels.network(network) || "—"}</span>
          <span className="ml-auto text-xs text-muted-foreground">{t("destination_network_only")}</span>
        </div>
      )}

      <ColocaForm form={form} onSubmit={() => undefined} className="space-y-4">
        <fieldset disabled={disabled} className="m-0 space-y-4 border-0 p-0">
          <DestinationPicker
            network={network}
            wallets={usable}
            canSave={canSave}
            disabled={disabled}
            variant={variant}
          />
          <ColocaAlert
            variant="warning"
            icon={TriangleAlert}
            text={
              refund
                ? t("sell_refund_warning", { network: labels.network(network) })
                : t("destination_onchain_warning", { asset, network: labels.network(network) })
            }
          />
          <ColocaInputField
            name="concept"
            label={t("concept_label")}
            placeholder={t("concept_placeholder")}
            maxLength={CONCEPT_MAX_LENGTH}
            autoComplete="off"
          />
        </fieldset>
      </ColocaForm>
    </section>
  );
}

function DestinationPicker({
  network,
  wallets,
  canSave,
  disabled,
  variant,
}: {
  network: string;
  wallets: OtcSavedWallet[];
  canSave: boolean;
  disabled?: boolean;
  variant: WalletSectionVariant;
}) {
  const { t } = useTranslations("cryptoSwaps");
  const selectId = useId();
  const { setValue } = useFormContext<DestinationValues>();
  const savedWalletId = useWatch<DestinationValues, "savedWalletId">({ name: "savedWalletId" });
  const address = useWatch<DestinationValues, "address">({ name: "address" });
  const selected = wallets.find((wallet) => wallet.id === savedWalletId) ?? null;

  const select = (id: string) => {
    const wallet = wallets.find((candidate) => candidate.id === id);
    setValue("savedWalletId", wallet?.id ?? null, { shouldValidate: true });
    setValue("address", wallet?.address ?? "", { shouldValidate: true });
    setValue("save", false, { shouldValidate: true });
  };

  // A typed address that is already saved is that wallet: no second save, and the order names it.
  useEffect(() => {
    if (savedWalletId) return;
    const typed = (address ?? "").trim().toLowerCase();
    const match = typed ? wallets.find((wallet) => wallet.address.toLowerCase() === typed) : undefined;
    if (match) {
      setValue("savedWalletId", match.id, { shouldValidate: true });
      setValue("save", false, { shouldValidate: true });
    }
  }, [address, savedWalletId, wallets, setValue]);

  // A deleted wallet, or one saved on another network, must not stay selected.
  useEffect(() => {
    if (savedWalletId && !selected) {
      setValue("savedWalletId", null, { shouldValidate: true });
      setValue("address", "", { shouldValidate: true });
    }
  }, [savedWalletId, selected, setValue]);

  return (
    <div className="flex flex-col gap-4">
      {wallets.length > 0 && (
        <div className="flex flex-col gap-2">
          <label htmlFor={selectId} className="text-sm font-medium text-foreground">
            {variant === "refund" ? t("sell_refund_saved_label") : t("saved_wallets_label")}
          </label>
          <ColocaSelect
            id={selectId}
            value={savedWalletId ?? NEW_ADDRESS}
            onValueChange={select}
            disabled={disabled}
            options={[
              ...wallets.map((wallet) => ({
                value: wallet.id,
                label: `${wallet.alias} · ${shortAddress(wallet.address)}`,
              })),
              { value: NEW_ADDRESS, label: t("saved_wallets_new") },
            ]}
          />
        </div>
      )}

      {selected ? (
        <SavedWalletCard wallet={selected} />
      ) : (
        <>
          <AddressField network={network} variant={variant} />
          {canSave && <SaveWalletFields variant={variant} />}
        </>
      )}
    </div>
  );
}

function SavedWalletCard({ wallet }: { wallet: OtcSavedWallet }) {
  const { t } = useTranslations("cryptoSwaps");
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-border bg-card p-4">
      <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <BookmarkCheck aria-hidden="true" className="size-4 text-primary" />
        {wallet.alias}
      </span>
      <span className="font-mono text-sm break-all text-foreground">{wallet.address}</span>
      <span className="text-xs text-muted-foreground">
        {wallet.ownership === "OWN"
          ? t("saved_wallet_owner_own")
          : t("saved_wallet_owner_third_party", { holder: wallet.holder_name ?? "—" })}
      </span>
    </div>
  );
}

function SaveWalletFields({ variant }: { variant: WalletSectionVariant }) {
  const { t } = useTranslations("cryptoSwaps");
  const refund = variant === "refund";
  const save = useWatch<DestinationValues, "save">({ name: "save" });
  const ownership = useWatch<DestinationValues, "ownership">({ name: "ownership" });

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-xl border p-4",
        save ? "border-primary/40" : "border-border",
      )}
    >
      <ColocaCheckboxField
        name="save"
        label={refund ? t("sell_refund_save_label") : t("saved_wallet_save_label")}
        description={t("saved_wallet_save_hint")}
      />
      {save && (
        <>
          <ColocaInputField
            name="alias"
            label={t("saved_wallet_alias_label")}
            placeholder={t("saved_wallet_alias_placeholder")}
            maxLength={SAVED_WALLET_ALIAS_MAX_LENGTH}
            autoComplete="off"
          />
          {!refund && (
            <ColocaRadioGroupField
              name="ownership"
              label={t("saved_wallet_ownership_label")}
              required
              items={[
                { value: "OWN", label: t("saved_wallet_ownership_own") },
                { value: "THIRD_PARTY", label: t("saved_wallet_ownership_third_party") },
              ]}
            />
          )}
          {!refund && ownership === "THIRD_PARTY" && (
            <ColocaInputField
              name="holderName"
              label={t("saved_wallet_holder_label")}
              placeholder={t("saved_wallet_holder_placeholder")}
              maxLength={120}
              autoComplete="off"
              required
            />
          )}
          <p className="text-[13px] leading-relaxed text-muted-foreground">{t("saved_wallet_declaration")}</p>
        </>
      )}
    </div>
  );
}

function AddressField({ network, variant }: { network: string; variant: WalletSectionVariant }) {
  const { t } = useTranslations("cryptoSwaps");
  const labels = useCryptoSwapLabels();
  const address = useWatch({ name: "address" }) as string | undefined;
  const check = checkCryptoSwapAddress(network, address ?? "");
  const networkShort = labels.networkShort(network);

  const verdict: { icon: LucideIcon | null; tone: string; text: string } =
    check.status === "valid"
      ? {
          icon: ShieldCheck,
          tone: "text-success-container-foreground",
          text: t("destination_address_valid", { network: networkShort }),
        }
      : check.status === "invalid"
        ? {
            icon: CircleX,
            tone: "text-destructive",
            text: t("destination_address_invalid", { network: networkShort }),
          }
        : check.status === "unsupported_network"
          ? { icon: TriangleAlert, tone: "text-destructive", text: t("destination_network_unsupported") }
          : {
              icon: null,
              tone: "text-muted-foreground",
              text: t(
                variant === "refund" ? "sell_refund_address_empty_hint" : "destination_address_empty_hint",
              ),
            };

  const VerdictIcon = verdict.icon;

  return (
    <div className="flex flex-col gap-2">
      <ColocaInputField
        name="address"
        label={variant === "refund" ? t("sell_refund_address_label") : t("destination_address_label")}
        placeholder={t("destination_address_placeholder")}
        autoComplete="off"
      />
      <p className={cn("flex flex-wrap items-center gap-2 text-[13px]", verdict.tone)}>
        {VerdictIcon && <VerdictIcon aria-hidden="true" className="size-4 shrink-0" />}
        <span>{verdict.text}</span>
      </p>
    </div>
  );
}

function shortAddress(address: string): string {
  return address.length > 14 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
}
